/**
 * Helpers de URL pra navegação a partir de UserBooks (estante do user).
 *
 * UserBooks têm 2 campos relevantes:
 *  - `bookId`: id original (pode ser legacy `apple-XXX`, `google-XXX`,
 *    ou canonical `apple:XXX`, `isbn:XXX`, `google:XXX`)
 *  - `canonicalBookId`: canonical resolvido em background pelo
 *    resolveBookIdentity. Populado quando o backend conseguiu resolver;
 *    undefined pra UserBooks muito antigos ou cujo enrichment falhou.
 *
 * Quando user clica num card de UserBook, queremos a URL mais limpa
 * possível pra evitar redirect intermediário (`useCanonicalIdRedirect`
 * faz o redirect, mas custa 1 read + flash de URL feia).
 *
 * Estratégia:
 *  1. `canonicalBookId` quando presente — URL canônica pronta.
 *  2. Fallback `bookId` — `useCanonicalIdRedirect` resolve em runtime
 *     (incluindo legacy `apple-XXX` que agora é normalizado).
 */

export interface UserBookLike {
  bookId: string;
  canonicalBookId?: string | null;
}

/**
 * Retorna o id mais limpo possível pra navegação. Prefere
 * `canonicalBookId` quando UserBook já tem resolvido.
 */
export function getUserBookNavId(userBook: UserBookLike): string {
  return userBook.canonicalBookId || userBook.bookId;
}

/**
 * Extrai o ISBN (10/13) de ids que o carregam: `isbn:X`, `brasilapi-X`,
 * `openlibrary-X`. Usado pra casar o user_book salvo pelo scan BR
 * (`bookId="brasilapi-{isbn}"`) com a URL canônica (`isbn:{isbn}`).
 */
function extractIsbnKey(id?: string | null): string | null {
  if (!id) return null;
  const m = id.match(/^(?:isbn:|brasilapi-|openlibrary-)(\d{10}(?:\d{3})?)$/);
  return m ? m[1] : null;
}

/**
 * Encontra um UserBook na estante que represente o livro identificado por
 * `lookupId`, tentando múltiplos campos. Necessário porque a URL pode chegar
 * em formatos variados:
 *   - canonical (`isbn:X`, `apple:X`, `google:X`) — formato novo SEO-friendly
 *   - legacy hyphen (`apple-X`, `google-X`) — UserBooks pré-refactor
 *   - raw Google volume id, brasilapi-X, etc — fallbacks
 *
 * E o UserBook pode ter sido salvo com `bookId` em qualquer um desses formatos
 * mais ter `canonicalBookId` resolvido só depois pelo background enrichment.
 *
 * Match em ordem de preferência:
 *  1. `userBook.bookId === lookupId` (match direto, caso comum)
 *  2. `userBook.canonicalBookId === lookupId` (URL canonical, doc legacy)
 *  3. Ambos com normalização `apple-X` ↔ `apple:X` (formato vs formato)
 *  4. Match por ISBN entre `isbn:X` ↔ `brasilapi-X`/`openlibrary-X` (scan BR)
 */
export function findUserBookByAnyId<T extends UserBookLike>(
  shelf: T[],
  lookupId: string,
): T | null {
  if (!shelf.length || !lookupId) return null;

  // Camada 1 — match direto em bookId
  const direct = shelf.find((b) => b.bookId === lookupId);
  if (direct) return direct;

  // Camada 2 — match no canonicalBookId
  const byCanonical = shelf.find((b) => b.canonicalBookId === lookupId);
  if (byCanonical) return byCanonical;

  // Camada 3 — normaliza hyphen ↔ colon dos dois lados.
  // Ex: lookupId="apple-657046481", userBook.bookId="apple:657046481"
  // ou vice-versa.
  const lookupNormalized = lookupId.replace(/^(apple|google)-/, "$1:");
  const lookupHyphen = lookupId.replace(/^(apple|google):/, "$1-");
  const byNormalized = shelf.find((b) => {
    const bookIdNorm = b.bookId.replace(/^(apple|google)-/, "$1:");
    return (
      bookIdNorm === lookupNormalized ||
      b.bookId === lookupHyphen ||
      b.canonicalBookId === lookupNormalized
    );
  });
  if (byNormalized) return byNormalized;

  // Camada 4 — match por ISBN. O scan BR salva `bookId="brasilapi-{isbn}"`
  // mas a URL canônica chega como `isbn:{isbn}` (bestEditionId do work).
  // Sem isso, o livro escaneado não acha o próprio user_book na BookDetail
  // (→ sem fallback de capa, ring de progresso quebrado).
  const lookupIsbn = extractIsbnKey(lookupId);
  if (lookupIsbn) {
    const byIsbn = shelf.find(
      (b) =>
        extractIsbnKey(b.bookId) === lookupIsbn ||
        extractIsbnKey(b.canonicalBookId) === lookupIsbn,
    );
    if (byIsbn) return byIsbn;
  }

  return null;
}
